// Input: one state object the game reads every frame, fed by keyboard, mouse and touch.
// Desktop: WASD / arrows move, SHIFT sprints, SPACE or E = ACTION (grab / stash / answer the call), mouse click = POP
// (Inspector), Q = WHISTLE. Touch (authored separately, docs/UI.md): a stick on the left half that follows the thumb,
// ACTION / SPRINT buttons (Sneak), POP / WHISTLE buttons (Inspector); a tap on a townie MARKS it, POP fires at the mark.
// The game never requests pointer lock (studio rule: only in real fullscreen, and nothing here needs it).

export interface InputState {
  mx: number; mz: number;          // world-space move vector (camera looks toward −Z, so screen-up = −Z)
  sprint: boolean;
  act: boolean;                    // held
  actPressed: boolean;             // edge this frame
  popAt: { x: number; y: number } | null;   // a click/tap in CSS px to pick a townie (consumed by the game)
  popFire: boolean;                // touch POP button
  whistle: boolean;                // edge
  touch: boolean;
}

export const input: InputState = { mx: 0, mz: 0, sprint: false, act: false, actPressed: false, popAt: null, popFire: false, whistle: false, touch: matchMedia("(pointer: coarse)").matches };
const keys = new Set<string>();
let enabled = true;
export const setEnabled = (v: boolean) => { enabled = v; if (!v) { keys.clear(); input.act = false; input.sprint = false; input.mx = input.mz = 0; } };

const MOVE: Record<string, [number, number]> = {
  // e.code is the PHYSICAL key, so these are the WASD positions on every layout (AZERTY players press Z/Q/S/D there)
  KeyW: [0, -1], ArrowUp: [0, -1], KeyS: [0, 1], ArrowDown: [0, 1], KeyA: [-1, 0], ArrowLeft: [-1, 0], KeyD: [1, 0], ArrowRight: [1, 0],
};

addEventListener("keydown", (e) => {
  if (!enabled || (e.target as HTMLElement)?.tagName === "INPUT") return;
  if (e.code === "Space" || e.code.startsWith("Arrow")) e.preventDefault();
  if (keys.has(e.code)) return;
  keys.add(e.code);
  if (e.code === "Space" || e.code === "KeyE" || e.code === "Enter") { input.act = true; input.actPressed = true; }
  if (e.code === "KeyQ" || e.code === "KeyF") input.whistle = true;
});
addEventListener("keyup", (e) => {
  keys.delete(e.code);
  if (e.code === "Space" || e.code === "KeyE" || e.code === "Enter") input.act = keys.has("Space") || keys.has("KeyE") || keys.has("Enter");
});
addEventListener("blur", () => { keys.clear(); input.act = false; input.sprint = false; });

/** called once per frame: fold held keys + the touch stick into the move vector */
export function poll() {
  let x = 0, z = 0;
  for (const k of keys) {
    const m = MOVE[k]; if (!m) continue;
    x += m[0]; z += m[1];
  }
  if (stick.active) { x = stick.x; z = stick.y; }
  const l = Math.hypot(x, z); if (l > 1) { x /= l; z /= l; }
  input.mx = enabled ? x : 0; input.mz = enabled ? z : 0;
  input.sprint = enabled && (keys.has("ShiftLeft") || keys.has("ShiftRight") || touchSprint);
}
/** clear the one-frame edges after the game read them */
export function endFrame() { input.actPressed = false; input.whistle = false; input.popAt = null; input.popFire = false; }

// ---- mouse: a click on the stage = a pick (the game decides if it is a POP)
export const hover = { x: -1, y: -1 };
export function bindStage(el: HTMLElement) {
  el.addEventListener("mousemove", (e) => { hover.x = e.clientX; hover.y = e.clientY; });
  el.addEventListener("mouseleave", () => { hover.x = hover.y = -1; });
  el.addEventListener("mousedown", (e) => { if (!enabled || e.button !== 0) return; input.popAt = { x: e.clientX, y: e.clientY }; });
  el.addEventListener("contextmenu", (e) => e.preventDefault());
  // touch: left half = the stick; a short tap anywhere else (or a tap that never moved) = a pick/mark
  el.addEventListener("touchstart", onTouchStart, { passive: false });
  el.addEventListener("touchmove", onTouchMove, { passive: false });
  el.addEventListener("touchend", onTouchEnd, { passive: false });
  el.addEventListener("touchcancel", onTouchEnd, { passive: false });
}

// ---- the stick: base where the thumb lands (left half), full speed at 40 % of the radius (townsfolk walk at one pace)
export const stick = { active: false, id: -1, bx: 0, by: 0, x: 0, y: 0, cx: 0, cy: 0, r: 56 };
let touchSprint = false;
export const setTouchSprint = (v: boolean) => { touchSprint = v; };
const taps = new Map<number, { x: number; y: number; t: number }>();

function onTouchStart(e: TouchEvent) {
  if (!enabled) return;
  e.preventDefault();
  input.touch = true;
  for (const t of Array.from(e.changedTouches)) {
    if (!stick.active && t.clientX < innerWidth * 0.5) {
      stick.active = true; stick.id = t.identifier; stick.bx = t.clientX; stick.by = t.clientY; stick.cx = t.clientX; stick.cy = t.clientY; stick.x = stick.y = 0;
      stick.r = Math.max(44, Math.min(72, Math.min(innerWidth, innerHeight) * 0.15));
    } else taps.set(t.identifier, { x: t.clientX, y: t.clientY, t: performance.now() });
  }
}
function onTouchMove(e: TouchEvent) {
  e.preventDefault();
  for (const t of Array.from(e.changedTouches)) {
    if (t.identifier !== stick.id) continue;
    let dx = t.clientX - stick.bx, dy = t.clientY - stick.by;
    const d = Math.hypot(dx, dy);
    if (d > stick.r) { const k = (d - stick.r) / d; stick.bx += dx * k; stick.by += dy * k; dx = t.clientX - stick.bx; dy = t.clientY - stick.by; }   // the base trails the thumb
    stick.cx = t.clientX; stick.cy = t.clientY;
    const m = Math.hypot(dx, dy), dead = Math.max(5, stick.r * 0.08);
    if (m < dead) { stick.x = stick.y = 0; continue; }
    const s = Math.min(1, m / (stick.r * 0.4));
    stick.x = (dx / m) * s; stick.y = (dy / m) * s;
  }
}
function onTouchEnd(e: TouchEvent) {
  e.preventDefault();
  for (const t of Array.from(e.changedTouches)) {
    if (t.identifier === stick.id) { stick.active = false; stick.id = -1; stick.x = stick.y = 0; continue; }
    const s = taps.get(t.identifier); taps.delete(t.identifier);
    if (s && performance.now() - s.t < 350 && Math.hypot(t.clientX - s.x, t.clientY - s.y) < 14) input.popAt = { x: t.clientX, y: t.clientY };
  }
}

// ---- on-screen buttons (the HUD builds them from the painted kit and wires them here)
export function bindHold(el: HTMLElement, on: (down: boolean) => void) {
  const down = (e: Event) => { e.preventDefault(); e.stopPropagation(); if (enabled) on(true); };
  const up = (e: Event) => { e.preventDefault(); e.stopPropagation(); on(false); };
  el.addEventListener("touchstart", down, { passive: false }); el.addEventListener("touchend", up, { passive: false }); el.addEventListener("touchcancel", up, { passive: false });
  el.addEventListener("mousedown", down); el.addEventListener("mouseup", up); el.addEventListener("mouseleave", up);
}
export const pressAction = (d: boolean) => { if (d && !input.act) input.actPressed = true; input.act = d; };
